import { useState, useEffect } from "react"
import { X } from "lucide-react"
import { Button } from "components/ui/button"
import { Label } from "components/ui/label"
import { Switch } from "components/ui/switch"
import { UserAvatar } from "components/ui/avatar"
import { settingsService, type UserSettings, type BackendUser } from "services/settingsService"
import { useTheme } from "contexts/ThemeContext"
import { useUser } from "contexts/UserContext"
import { WebPushSection } from "components/settings/WebPushSection"
import { NotificationPreferencesMatrix } from "components/settings/NotificationPreferencesMatrix"

type TabType = "generali" | "notifiche" | "piano"

// Interfaccia per i dati utente usati dall'app
export interface CurrentUser {
  id?: number
  first_name?: string
  last_name?: string
  email?: string
  username?: string
  role?: string
  is_superuser?: boolean
  language?: string
  working_hours?: string
  profile_photo_url?: string | null
}

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
  onUserUpdate?: (user: CurrentUser) => void
}

// Genera opzioni orario da 00:00 a 23:30 con incrementi di 30 minuti
const generateTimeOptions = () => {
  const options: string[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, "0")
      const minute = m.toString().padStart(2, "0")
      options.push(`${hour}:${minute}`)
    }
  }
  return options
}

const TIME_OPTIONS = generateTimeOptions()

// Giorni della settimana con abbreviazioni italiane
const DAYS_OF_WEEK = [
  { key: "mon", label: "L" },
  { key: "tue", label: "M" },
  { key: "wed", label: "M" },
  { key: "thu", label: "G" },
  { key: "fri", label: "V" },
  { key: "sat", label: "S" },
  { key: "sun", label: "D" },
]

// Default values per orario di lavoro
const DEFAULT_START_TIME = "09:00"
const DEFAULT_END_TIME = "18:00"
const DEFAULT_WORKING_DAYS = ["mon", "tue", "wed", "thu", "fri"]

// Parsing: converte stringa backend → state UI
// Formato nuovo: "HH:MM-HH:MM|day,day,day" (es. "09:00-18:00|mon,tue,wed,thu,fri")
// Formato vecchio: "lun-ven-9-18" → fallback ai default
const parseWorkingHours = (value: string | undefined): {
  startTime: string
  endTime: string
  days: string[]
} => {
  if (!value || !value.includes("|")) {
    return {
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME,
      days: DEFAULT_WORKING_DAYS,
    }
  }

  try {
    const [timePart, daysPart] = value.split("|")
    const [startTime, endTime] = timePart.split("-")
    const days = daysPart.split(",").filter((d) => d.trim() !== "")

    const validStart = TIME_OPTIONS.includes(startTime) ? startTime : DEFAULT_START_TIME
    const validEnd = TIME_OPTIONS.includes(endTime) ? endTime : DEFAULT_END_TIME
    const validDays = days.filter((d) =>
      DAYS_OF_WEEK.some((day) => day.key === d)
    )

    return {
      startTime: validStart,
      endTime: validEnd,
      days: validDays.length > 0 ? validDays : DEFAULT_WORKING_DAYS,
    }
  } catch {
    return {
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME,
      days: DEFAULT_WORKING_DAYS,
    }
  }
}

// Serializzazione: converte state UI → stringa backend
// Output: "HH:MM-HH:MM|day,day,day"
const serializeWorkingHours = (
  startTime: string,
  endTime: string,
  days: string[]
): string => {
  const sortedDays = DAYS_OF_WEEK
    .filter((d) => days.includes(d.key))
    .map((d) => d.key)
  return `${startTime}-${endTime}|${sortedDays.join(",")}`
}

const TABS: { id: TabType; label: string }[] = [
  { id: "generali", label: "Generali" },
  { id: "notifiche", label: "Notifiche" },
  { id: "piano", label: "Piano & Fatturazione" },
]

export function SettingsModal({ isOpen, onClose, onUserUpdate }: SettingsModalProps) {
  const { theme, setTheme } = useTheme()
  const { setUser } = useUser()
  const [activeTab, setActiveTab] = useState<TabType>("generali")
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Stato per il selettore orario di lavoro
  const [workStartTime, setWorkStartTime] = useState("09:00")
  const [workEndTime, setWorkEndTime] = useState("18:00")
  const [workingDays, setWorkingDays] = useState<string[]>(["mon", "tue", "wed", "thu", "fri"])

  // Load settings on mount
  useEffect(() => {
    if (isOpen) {
      loadSettings()
    }
  }, [isOpen])

  // Parse working_hours quando settings vengono caricati
  useEffect(() => {
    if (settings?.profile?.workingHours) {
      const parsed = parseWorkingHours(settings.profile.workingHours)
      setWorkStartTime(parsed.startTime)
      setWorkEndTime(parsed.endTime)
      setWorkingDays(parsed.days)
    }
  }, [settings?.profile?.workingHours])

  const loadSettings = async () => {
    setLoading(true)
    try {
      const data = await settingsService.getUserSettings()
      setSettings(data)
    } catch (error) {
      console.error("Error loading settings:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleThemeChange = (newTheme: "light" | "dark") => {
    // Tema gestito esclusivamente da ThemeContext (localStorage key: hexa-theme).
    // Non viene salvato dentro hexa_user_settings.preferences per evitare doppia fonte di verità.
    setTheme(newTheme)
  }

  const handleSave = async () => {
    if (!settings) return

    // Serializza working_hours dai state locali prima di salvare
    const serializedWorkingHours = serializeWorkingHours(workStartTime, workEndTime, workingDays)

    setSaving(true)
    try {
      // Aggiorna SOLO working_hours nel backend
      const updatedUser: BackendUser = await settingsService.updateProfile({
        workingHours: serializedWorkingHours,
      })

      // Aggiorna preferenze localmente
      await settingsService.updatePreferences(settings.preferences)

      console.log("[Settings] Saved successfully, working_hours:", serializedWorkingHours)

      // Aggiorna il UserContext con i dati dalla risposta API
      setUser({
        id: updatedUser.id,
        first_name: updatedUser.first_name,
        last_name: updatedUser.last_name,
        email: updatedUser.email,
        username: updatedUser.username,
        role: updatedUser.role,
        is_superuser: updatedUser.is_superuser,
        language: updatedUser.language,
        working_hours: serializedWorkingHours,
        profile_photo_url: updatedUser.profile_photo_url,
      })

      if (onUserUpdate) {
        onUserUpdate({
          id: updatedUser.id,
          first_name: updatedUser.first_name,
          last_name: updatedUser.last_name,
          email: updatedUser.email,
          username: updatedUser.username,
          role: updatedUser.role,
          is_superuser: updatedUser.is_superuser,
          language: updatedUser.language,
          working_hours: serializedWorkingHours,
          profile_photo_url: updatedUser.profile_photo_url,
        })
      }

      onClose()
    } catch (error) {
      console.error("Error saving settings:", error)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-[var(--hexa-overlay)] z-[100] animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed top-12 right-6 z-[101] w-full max-w-md animate-in slide-in-from-top duration-200">
        <div className="bg-[var(--hexa-modal-bg)] rounded-lg shadow-2xl flex flex-col max-h-[calc(100vh-4rem)]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-[rgba(106,77,255,0.25)]">
            <h2 className="text-[var(--hexa-text-primary)] text-[13px] font-medium">Impostazioni</h2>
            <button
              onClick={onClose}
              className="text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Tabs orizzontali */}
          <div className="px-4">
            <div className="flex border-b border-[rgba(106,77,255,0.25)]">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative px-4 py-2.5 text-[11px] font-medium transition-colors duration-200 ${
                    activeTab === tab.id
                      ? "text-[var(--hexa-purple)]"
                      : "text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)]"
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--hexa-purple)] rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 px-4 py-4 overflow-y-auto scrollbar-hexa">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-[var(--hexa-text-secondary)]">Caricamento...</span>
              </div>
            ) : settings ? (
              <>
                {/* TAB 1: GENERALI */}
                {activeTab === "generali" && (
                  <div className="space-y-5">
                    {/* Sezione 1 — Informazioni account */}
                    <div>
                      <h4 className="text-[var(--hexa-text-secondary)] text-[10px] font-semibold uppercase tracking-wider mb-3">
                        Informazioni account
                      </h4>
                      <div className="bg-[var(--hexa-surface-hover)] rounded-lg p-3 border border-[rgba(106,77,255,0.25)]">
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            firstName={settings.profile.firstName}
                            lastName={settings.profile.lastName}
                            imageUrl={settings.profile.profilePhoto || undefined}
                            size="lg"
                            className="rounded-xl"
                          />
                          <div className="flex-1 space-y-0.5">
                            <div className="flex items-baseline gap-2">
                              <span className="text-[var(--hexa-text-primary)] text-[12px] font-medium">
                                {settings.profile.firstName} {settings.profile.lastName}
                              </span>
                              <span className="text-[9px] px-2 py-0.5 rounded-full bg-[var(--hexa-purple)]/15 text-[var(--hexa-purple)] font-medium">
                                {settings.profile.role || "Utente"}
                              </span>
                            </div>
                            <span className="text-[var(--hexa-text-secondary)] text-[11px]">
                              {settings.profile.email}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sezione 2 — Orari di lavoro */}
                    <div>
                      <h4 className="text-[var(--hexa-text-secondary)] text-[10px] font-semibold uppercase tracking-wider mb-3">
                        Orari di lavoro
                      </h4>
                      <div className="bg-[var(--hexa-surface-hover)] rounded-lg p-3 border border-[rgba(106,77,255,0.25)] space-y-3">
                        {/* Riga 1: Selettori orario */}
                        <div className="flex items-center gap-3">
                          <span className="text-[var(--hexa-text-secondary)] text-[10px]">Dalle</span>
                          <select
                            value={workStartTime}
                            onChange={(e) => setWorkStartTime(e.target.value)}
                            className="bg-[var(--hexa-surface)] border border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] text-[11px] h-7 rounded-md px-1.5 min-w-[65px]"
                          >
                            {TIME_OPTIONS.map((time) => (
                              <option key={`start-${time}`} value={time}>
                                {time}
                              </option>
                            ))}
                          </select>
                          <span className="text-[var(--hexa-text-secondary)] text-[10px]">Alle</span>
                          <select
                            value={workEndTime}
                            onChange={(e) => setWorkEndTime(e.target.value)}
                            className="bg-[var(--hexa-surface)] border border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] text-[11px] h-7 rounded-md px-1.5 min-w-[65px]"
                          >
                            {TIME_OPTIONS.map((time) => (
                              <option key={`end-${time}`} value={time}>
                                {time}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Riga 2: Toggle giorni lavorativi */}
                        <div className="flex items-center gap-1.5">
                          {DAYS_OF_WEEK.map((day) => {
                            const isActive = workingDays.includes(day.key)
                            return (
                              <button
                                key={day.key}
                                type="button"
                                onClick={() => {
                                  if (isActive) {
                                    setWorkingDays(workingDays.filter((d) => d !== day.key))
                                  } else {
                                    setWorkingDays([...workingDays, day.key])
                                  }
                                }}
                                className="w-6 h-6 rounded-full text-[10px] font-medium transition-colors flex items-center justify-center"
                                style={{
                                  backgroundColor: isActive
                                    ? "var(--hexa-purple)"
                                    : "var(--hexa-button-cancel)",
                                  color: isActive
                                    ? "white"
                                    : "var(--hexa-button-cancel-text)",
                                }}
                                onMouseEnter={(e) => {
                                  if (!isActive) {
                                    e.currentTarget.style.backgroundColor = "var(--hexa-button-cancel-hover)"
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isActive) {
                                    e.currentTarget.style.backgroundColor = "var(--hexa-button-cancel)"
                                  }
                                }}
                              >
                                {day.label}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Sezione 3 — Preferenze */}
                    <div>
                      <h4 className="text-[var(--hexa-text-secondary)] text-[10px] font-semibold uppercase tracking-wider mb-3">
                        Preferenze
                      </h4>
                      <div className="bg-[var(--hexa-surface-hover)] rounded-lg p-3 border border-[rgba(106,77,255,0.25)] space-y-4">
                        {/* Tema */}
                        <div>
                          <Label className="text-[var(--hexa-text-secondary)] text-[10px]">Tema</Label>
                          <div className="flex gap-2 mt-1.5">
                            {[
                              { id: "dark" as const, label: "Scuro", icon: "🌙" },
                              { id: "light" as const, label: "Chiaro", icon: "☀️" },
                            ].map((t) => (
                              <button
                                key={t.id}
                                onClick={() => handleThemeChange(t.id)}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-[11px] font-medium transition-all ${
                                  theme === t.id
                                    ? "bg-[var(--hexa-purple)]/15 text-[var(--hexa-purple)] border border-[var(--hexa-purple)]/30"
                                    : "bg-[var(--hexa-surface)] text-[var(--hexa-text-secondary)] border border-[rgba(106,77,255,0.25)] hover:border-[var(--hexa-text-secondary)]"
                                }`}
                              >
                                <span>{t.icon}</span>
                                {t.label}
                              </button>
                            ))}
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 2: NOTIFICHE */}
                {activeTab === "notifiche" && (
                  <div className="space-y-5">
                    <WebPushSection />
                    <NotificationPreferencesMatrix />
                  </div>
                )}

                {/* TAB 3: PIANO & FATTURAZIONE */}
                {activeTab === "piano" && (
                  <div className="space-y-4">
                    {/* Banner Beta */}
                    <div className="relative overflow-hidden rounded-lg border border-[var(--hexa-purple)]/20 bg-gradient-to-r from-[var(--hexa-purple)]/10 via-[var(--hexa-purple)]/5 to-transparent p-4">
                      <div className="relative">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--hexa-purple)] bg-[var(--hexa-purple)]/15 px-2 py-0.5 rounded mb-1.5 inline-block">
                          Beta
                        </span>
                        <p className="text-[var(--hexa-text-secondary)] text-[11px] leading-relaxed">
                          Stai utilizzando Hexa durante la fase{" "}
                          <strong className="text-[var(--hexa-text-primary)]">beta gratuita</strong>.
                          Al momento tutte le funzionalità sono incluse senza costi.
                        </p>
                      </div>
                    </div>

                    {/* Pricing Cards — dettagli oscurati */}
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { name: "Starter", lines: 3 },
                        { name: "Pro", lines: 5, highlighted: true },
                        { name: "Enterprise", lines: 4 },
                      ].map((plan) => (
                        <div
                          key={plan.name}
                          className={`rounded-lg border p-4 flex flex-col ${
                            plan.highlighted
                              ? "border-[var(--hexa-purple)]/20 bg-[var(--hexa-purple)]/[0.04]"
                              : "border-[rgba(106,77,255,0.25)] bg-[var(--hexa-surface)]"
                          }`}
                        >
                          {/* Nome piano — visibile */}
                          <h4 className="text-[var(--hexa-text-primary)] text-[13px] font-bold mb-3">
                            {plan.name}
                          </h4>

                          {/* Prezzo — blurred */}
                          <div className="mb-4">
                            <div className="h-7 w-20 rounded bg-[var(--hexa-surface-hover)] blur-[6px]" />
                            <div className="h-3 w-28 rounded bg-[var(--hexa-surface-hover)] blur-[4px] mt-2" />
                          </div>

                          {/* Feature lines — blurred */}
                          <div className="flex-1 space-y-2.5 mb-4">
                            {Array.from({ length: plan.lines }).map((_, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-[var(--hexa-surface-hover)] blur-[3px] flex-shrink-0" />
                                <div
                                  className="h-2.5 rounded bg-[var(--hexa-surface-hover)] blur-[4px]"
                                  style={{ width: `${60 + (i * 13) % 35}%` }}
                                />
                              </div>
                            ))}
                          </div>

                          {/* CTA — blurred */}
                          <div className="h-8 rounded-md bg-[var(--hexa-surface-hover)] blur-[5px]" />
                        </div>
                      ))}
                    </div>

                    {/* Nota informativa */}
                    <div className="bg-[var(--hexa-surface-hover)] rounded-lg border border-[rgba(106,77,255,0.25)] p-3 text-center">
                      <p className="text-[var(--hexa-text-secondary)] text-[11px] mb-0.5">
                        I piani a pagamento sono in fase di definizione.
                      </p>
                      <p className="text-[var(--hexa-text-muted)] text-[10px]">
                        Ti avviseremo in anticipo quando saranno disponibili.
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>

          {/* Footer — visibile solo nel tab Generali */}
          {activeTab === "generali" && (
            <div className="px-4 py-3 flex items-center justify-end gap-2.5 border-t border-[rgba(106,77,255,0.25)]">
              <Button
                onClick={onClose}
                className="bg-[var(--hexa-button-secondary)] hover:bg-[var(--hexa-surface-hover)] text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] h-7 px-4 text-[11px] font-normal"
              >
                Annulla
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white shadow-lg shadow-purple-900/30 h-7 px-4 text-[11px] font-normal"
              >
                {saving ? "Salvataggio..." : "Salva"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
